import { useEffect, useMemo } from 'react'
import { TdsButton } from '@trv-tds/react'
import dayjs from 'dayjs'
import { Field, reduxForm, reset } from 'redux-form'

import { exactly, required, valid, validator } from '../lib/validators'
import {
  ButtonContainer,
  Card,
  CardHeader,
  PageInformation
} from '../shared/styledComponents'
import { ClaimInput, DateOfLossPicker, RoleSelect } from './ClaimFormComponents'
import type { ClaimFormType } from './types'

const normalizeClaimNumber = (value: string) =>
  value && value.replace(/[^a-zA-Z\d]/g, '').toUpperCase().slice(0, 7)

type ClaimFormProps = {
  disabled: boolean
  handleSubmit: (event: React.SyntheticEvent) => void
  roleSelect: (event: React.FormEvent<HTMLSelectElement>) => void
  requesting: boolean
  disableContinue: boolean
  setIsFocused: (isFocused: boolean) => void
  formIncomplete: boolean
  roleDisabled: boolean
  isAuto: boolean
}

const ClaimFormComponent = (props: ClaimFormProps) => {
  const {
    disabled,
    handleSubmit,
    requesting,
    roleSelect,
    disableContinue,
    setIsFocused,
    formIncomplete,
    roleDisabled,
    isAuto
  } = props

  useEffect(() => {
    reset('claimForm')
  }, [])

  const RoleSelectComponent = useMemo(() => {
    return RoleSelect.bind(null, isAuto)
  }, [isAuto])

  return (
    <form id='claimForm' autoComplete='off' onSubmit={handleSubmit}>
      <Card>
        <CardHeader>Find Claim</CardHeader>
        <PageInformation>
          {disabled
            ? 'Click "Continue" to proceed to the next screen.'
            : 'Help us find your claim by entering the claim number, incident date and your role '
              + 'in the claim.'
          }
        </PageInformation>
        <Field
          id='claimNumber'
          data-cy='claimNumber'
          name='claimNumber'
          label='Claim Number'
          autoCapitalize='true'
          required={true}
          autoFocus={true} // eslint-disable-line jsx-a11y/no-autofocus
          type='text'
          role='textbox'
          normalize={normalizeClaimNumber}
          component={ClaimInput}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
        />
        <Field
          label='Date of Loss'
          id='dateOfLoss'
          name='dateOfLoss'
          role='group'
          component={DateOfLossPicker}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={disabled}
          required={!disabled}
        />
        <Field
          id='roleInClaim'
          name='roleInClaim'
          label='Role'
          type='select'
          role='combobox'
          required={true}
          component={RoleSelectComponent}
          onChange={roleSelect}
          disabled={disabled || roleDisabled}
        />
      </Card>
      <ButtonContainer>
        <TdsButton
          id='claimContinueButton'
          variant='primary'
          busy={requesting}
          disabled={disableContinue || formIncomplete || requesting}
          buttonType='submit'
        >
          Continue
        </TdsButton>
      </ButtonContainer>
    </form>
  )

}
const claimMessage = 'Please enter a valid claim number.'
const dateMessage = 'Please enter a valid date of loss.'

const exactly7 = exactly(7, claimMessage)
const allowOnlyLetterAndNumber = valid(/^[0-9a-zA-Z]+$/, claimMessage)
const notEmptyWithLetterAndNumber =
  validator(required(claimMessage), exactly7, allowOnlyLetterAndNumber)

const exactly10date = exactly(10, dateMessage)
const noFutureDate = (value: string) =>
  dayjs(value, 'MM/DD/YYYY').isAfter(dayjs(dayjs(), 'MM/DD/YYYY'))
    ? dateMessage
    : undefined


const validDate = (value: string) =>
  dayjs(value, 'MM/DD/YYYY').isValid()
    ? undefined
    : dateMessage

const notEmptyValidAndCurrentDate =
  validator(required(dateMessage), exactly10date, validDate, noFutureDate)

const validate = (values: ClaimFormType) => ({
  claimNumber: notEmptyWithLetterAndNumber(values.claimNumber),
  dateOfLoss: notEmptyValidAndCurrentDate(values.dateOfLoss),
  roleInClaim: required('Please select a role.')(values.roleInClaim)
})

const ClaimForm = reduxForm<ClaimFormType, ClaimFormProps>({
  form: 'claimForm',
  validate,
  destroyOnUnmount: false,
  touchOnChange: true,
  touchOnBlur: false
})(ClaimFormComponent)

export {
  ClaimForm
}
